import React, { useState, useEffect, useContext, createContext } from "react"
import { useModal } from "lib/modal"
import { useApolloClient } from "@apollo/react-hooks"
import { useAuth } from "lib/auth"
import { useTranslation } from "lib/i18n/translation"
import { i18n } from "lib/i18n"
import isBrowser from "lib/tools/is-browser"

import {
	FETCH_COUNTRY,
	FETCH_PLATFORM_ACCESS
} from "lib/graphql/queries"
import moment from "moment"

import FromAnotherCountry from "components/modals/from-another-country"

const countryContext = createContext()

export const CountryConsumer = countryContext.Consumer

// Provider component that wraps your app and makes country object ...
// ... available to any child component that calls useCountry().
export const ProvideCountry = ({ children, country: initialCountry, countries, availableCountry, access, originalAccess, lang, asPath, env, subscriptions }) => {
	const country = useProvideCountry(initialCountry, countries, availableCountry, access, originalAccess, lang, asPath, env, subscriptions)

	return (
		<countryContext.Provider value={country}>
			{children}
		</countryContext.Provider>
	)
}

// Hook for child components to get the country object ...
// ... and re-render when it changes.
export const useCountry = () => {
	return useContext(countryContext)
}

const frenchDROMCOM = ["RE", "AD", "GP", "GF", "MQ", "YT", "MC", "NC", "PF", "BL", "MF", "PM", "WF"]
const euroCountries = ["DE", "AT", "BE", "BG", "CY", "HR", "DK", "ES", "EE", "FI", "FR", "GR", "HU", "IE", "IT", "LV", "LT", "MT", "LU", "NL", "PL", "PT", "CZ", "RO", "SK", "SI", "SE", ...frenchDROMCOM]

// Provider hook that creates country object and handles state
function useProvideCountry(initialCountry, initialCountries, availableCountry, initialAccess, initialOriginalAccess, lang, asPath, originalEnv, readSubscriptions) {
	const client = useApolloClient()
	const { show } = useModal()
	const { c } = useTranslation("common")
	const { user, onSignedIn, onSignedOut } = useAuth()

	try {
		// Set renewable prop on subscriptions
		initialCountries.forEach(country => {
			country.subscriptions.forEach(subscription => {
				const baseSubscription = readSubscriptions.find(s => s.id == subscription.id)

				subscription.renewable = false

				if (baseSubscription) {
					subscription.renewable = baseSubscription.extension.autoRenewal
				}
			})
		})
	} catch (e) {
		console.log(e)
	}

	const [isCountryLoaded, setIsCountryLoaded] = useState(false)
	const [currentIsoCode, setCurrentIsoCode] = useState()
	const [countries, setCountries] = useState(initialCountries)
	const [country, setCountry] = useState(initialCountry)
	const [available, setAvailable] = useState(availableCountry)
	const [userCountry, setUserCountry] = useState()
	const [svod, setSvod] = useState(initialCountry.svod)
	const [access, setAccess] = useState(initialAccess)
	const [originalAccess, setOriginalAccess] = useState(initialOriginalAccess)
	const [env, setEnv] = useState(originalEnv)

	const currentLang = i18n.language || lang
	const [countrySlug, setCountrySlug] = useState(country.default_language == currentLang ? country.isoCode : `${country.isoCode}-${currentLang}`)

	const updateCountry = (user) => {
		if (user) {
			return fetchCountry(user.profile.address.country.isoCode.toLowerCase(), true)
		} else {
			return client.query({
				query: FETCH_PLATFORM_ACCESS,
				fetchPolicy: "network-only"
			})
				.then(({ data: { platform: { access: { purchase }, i18n: { country: { isoCode } } } } }) => {
					setCurrentIsoCode(isoCode)
					setOriginalAccess({ country: isoCode.toLowerCase(), purchase })

					return fetchCountry(isoCode.toLowerCase(), true)
				})
		}
	}

	const fetchCountry = (isoCode) => {
		if (["re", "ad", "gp", "gf", "mq", "yt", "mc", "nc", "pf", "bl", "mf", "pm", "wf"].includes(isoCode.toLowerCase())) {
			isoCode = "fr"
		}
		return client.query({
			query: FETCH_COUNTRY,
			variables: { isoCode, prod: process.env.LACINETEK_ENV == "production" },
			context: { clientName: "admin" },
			fetchPolicy: "network-only"
		})
			.then(({ data: { country } }) => {
				setUserCountry(country)

				setIsCountryLoaded(true)
			})
	}

	useEffect(() => {
		updateCountry()
		onSignedIn("country", updateCountry)
		onSignedOut("country", updateCountry)
	}, [])

	useEffect(() => {
		setCountrySlug(country.default_language == i18n.language ? country.isoCode : `${country.isoCode}-${i18n.language}`)
	}, [i18n.language])

	const showAnotherCountry = () => {
		show({
			id: "from-another-country",
			title: c("you-cannot-buy-from-another-country"),
			children: <FromAnotherCountry />,
			className: "modal"
		})
	}

	const isMonthlySubscription = (subscription) => {
		const countrySubscription = country.subscriptions.find(countrySubscription => subscription.id == countrySubscription.id)

		if (!countrySubscription) {
			return false
		}

		return countrySubscription.key.includes("monthly")
	}

	const isYearlySubscription = (subscription) => {
		const countrySubscription = country.subscriptions.find(countrySubscription => subscription.id == countrySubscription.id)

		if (!countrySubscription) {
			return false
		}

		return countrySubscription.key.includes("annual")
	}

	const userTryToAccessToFrFromDROMCOM = access.country == "fr" && frenchDROMCOM.includes(originalAccess.country.toUpperCase())

	const originCountry = isBrowser
		? countries.find(c => c.isoCode == originalAccess.country.toLowerCase() || userTryToAccessToFrFromDROMCOM && c.isoCode == "fr")
		: null

	const userCountryExists = userCountry && countries.find(c => c.isoCode == userCountry.isoCode)
	const defaultCountry = countries.find(c => c.isoCode == "fr")

	let forceSvod = false
	if (user && country.svod_white_list) {
		forceSvod = country.svod_white_list.split(";").includes(user.profile.email)
	}

	return {
		isCountryLoaded,
		countries,
		country,
		originCountry,
		userCountryExists,
		userCountry,
		filterCountry: user ? (userCountryExists ? userCountry : defaultCountry) : country,
		inEuro: euroCountries.includes(currentIsoCode),
		svodAvailable: forceSvod || svod,
		svodAvailableSoon: !forceSvod && (svod && country.svod_from && moment(country.svod_from) > moment()),
		svodFrom: forceSvod ? null : country.svod_from,
		originalCountry: !isCountryLoaded ? true : userCountry?.isoCode == country?.isoCode && originCountry?.isoCode == userCountry?.isoCode,
		originalAccess: isCountryLoaded ? (access.country == originalAccess.country) || userTryToAccessToFrFromDROMCOM : true,
		accesses: {
			access,
			originalAccess
		},
		available,
		purchasable: isCountryLoaded &&
			!!originCountry &&
			!!userCountry &&
			!!country &&
			userCountry.isoCode == country.isoCode &&
			(available || access.purchase),
		showAnotherCountry,
		countrySlug,

		regex: new RegExp(`^/(${countries.map(country => country.isoCode.toLowerCase()).join('|')})`),
		asPath,
		env,
		isMonthlySubscription,
		isYearlySubscription
	}
}
